import React, { createContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config/api';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const API_URL = API_BASE_URL;
    
    const loadUser = useCallback(async () => {
        const localToken = localStorage.getItem('token');
        if (localToken) {
            axios.defaults.headers.common['x-auth-token'] = localToken;
            try {
                const res = await axios.get(`${API_URL}/api/auth`);
                // Add id property for consistency (backend returns _id)
                const userData = { ...res.data, id: res.data._id };
                setUser(userData);
            } catch (err) {
                console.error('Failed to load user, token might be invalid.', err);
                // Token geçersizse temizlik yap
                localStorage.removeItem('token');
                setToken(null);
                setUser(null);
                delete axios.defaults.headers.common['x-auth-token'];
            }
        }
        setLoading(false);
    }, [API_URL]);

    useEffect(() => {
        loadUser();
    }, [loadUser]);

    const login = async (email, password) => {
        const config = { headers: { 'Content-Type': 'application/json' } };
        const body = JSON.stringify({ email, password });
        try {
            const res = await axios.post(`${API_URL}/api/users/login`, body, config);
            localStorage.setItem('token', res.data.token);
            setToken(res.data.token); // Token'ı state'e ata
            await loadUser(); // Giriş sonrası kullanıcı bilgilerini hemen yükle
            return { success: true };
        } catch (err) {
            console.error('Login error:', err.response?.data);
            const errorMsg = err.response?.data?.errors?.[0]?.msg 
                || err.response?.data?.msg 
                || 'Failed to login';
            return { success: false, message: errorMsg };
        }
    };

    const register = async (name, email, password) => {
        const config = { headers: { 'Content-Type': 'application/json' } };
        const body = JSON.stringify({ name, email, password });
        try {
            const res = await axios.post(`${API_URL}/api/users/register`, body, config);
            localStorage.setItem('token', res.data.token);
            setToken(res.data.token);
            await loadUser();
            return { success: true };
        } catch (err) {
            console.error('Register error:', err.response?.data);
            // Backend'den gelen validation errors veya msg
            const errorMsg = err.response?.data?.errors?.[0]?.msg 
                || err.response?.data?.msg 
                || 'An error occurred';
            return { success: false, message: errorMsg };
        }
    };

    const logout = () => {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
        delete axios.defaults.headers.common['x-auth-token'];
    };

    const loginWithGoogle = async (credential) => {
        const config = { headers: { 'Content-Type': 'application/json' } };
        const body = JSON.stringify({ credential });
        try {
            const res = await axios.post(`${API_URL}/api/auth/google`, body, config);
            localStorage.setItem('token', res.data.token);
            localStorage.setItem('user', JSON.stringify(res.data.user));
            setToken(res.data.token);
            setUser(res.data.user);
            axios.defaults.headers.common['x-auth-token'] = res.data.token;
            return { success: true, user: res.data.user };
        } catch (err) {
            console.error('Google login error:', err.response?.data);
            const errorMsg = err.response?.data?.msg || 'Google authentication failed';
            return { success: false, message: errorMsg };
        }
    };

    return (
        <AuthContext.Provider value={{ token, user, setUser, register, login, loginWithGoogle, logout, loading }}>
            {children}
        </AuthContext.Provider>
    );
};
